import { faCheck, faCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useFeatureValue } from '@growthbook/growthbook-react';
import {
  CheckCircle,
  MoreVert,
  RadioButtonUncheckedOutlined,
} from '@mui/icons-material';
import StarIcon from '@mui/icons-material/Star';
import {
  Grid,
  IconButton,
  Stack,
  Dialog,
  DialogContent,
  Popover,
  Chip as MuiChip,
} from '@mui/material';
import React, { useRef, useCallback, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useModal } from 'react-modal-hook';
import { useNavigate } from 'react-router-dom';
import { HeaderDialog, TitleDialog } from '@/components/Modal';
import { TimesheetItem } from '../../TimesheetList';
import { getTimeSheetStatus, JobStatus, TimeSheetStatus } from '../../util';
import ProfileModal from '../ProfileModal';
import ProfileModalRevamp from '../ProfileModal/ProfileModalRevamp';
import Button from '@/components/Button';
import {
  HiredChip,
  WorkerPayRate,
  showNeedsApprovalAndReviewChip,
  MissingTimesheetChip,
  MissingCheckoutChip,
} from '../workerUtils';
import { timesheetFlagMessage } from '../../util';
import colors from '@/styles/colors';

import InlineTimesheetView from './InlineTimesheetView';
import OptionsModal from './OptionsModal';
import ResolveTimesheetModal from './ResolveTimesheetModal';
import EnterHoursModal from './EnterHoursModal';

import {
  BlockIconWithBackground,
  EditTimesheetIcon,
  GravyTrained,
  HeartFillIcon,
  HeartIcon,
  InfoItalicIcon,
} from '@/assets/icons';
import AddRatingIcon from '@/assets/icons/AddRatingIcon';
import ApprovedByIcon from '@/assets/icons/ApprovedByIcon';
import CommentCounterIcon from '@/assets/icons/CommentCounterIcon';
import Avatar from '@/components/Avatar';
import IconicButton from '@/components/IconicButton';
import RatingField from '@/components/RatingField';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import styled from '@/styles';
import { timesheetColors } from '@/styles/colors';
import {
  GetJobQuery,
  JobWorkerStatusEnum,
  RelationshipKindEnum,
  TimesheetStatusEnum,
} from '@/types/graphql';
import { containsGravyCertification, workerName } from '@/util/worker';
import PermissionComponent from '@/routes/PermissionComponent';
import useAuth from '@/hooks/useAuth';
import ScrutinyDialogPhoneView from '@/routes/Agency/ScrutinyDialogPhoneView';
import { NoWrapSpan } from '../style';

const CardHeading = styled('h6', {
  fontSize: '14px',
  fontWeight: '600',
  color: '#262626',
  margin: '0',
  wordBreak: 'break-word',
});

const CardCriteria = styled('span', {
  fontSize: '12px',
  fontWeight: '600',
  color: '#262626',
});

const CardCriteriaTotal = styled('span', {
  fontSize: '12px',
  fontWeight: '400',
  color: '#7A7A7A',
});

const TotalText = styled('p', {
  fontSize: '13px',
  fontWeight: '400',
  color: '#01483D',
  span: {
    fontWeight: '700',
  },
});

const ApprovalAlert = styled('p', {
  fontSize: '12px',
  fontWeight: '400',
  color: '#fff',
  background: '#ECA300',
  padding: '4px 8px',
  borderRadius: '8px',
  whiteSpace: 'nowrap',
});

const Counter = styled('span', {
  color: '#FFF',
  fontSize: '11px',
  fontWeight: '500',
  position: 'absolute',
  textAlign: 'center',
});

interface Props {
  workerInfo: any;
  disableEdit?: boolean;
  workerStatusChip?: React.ReactNode;
  totalTime?: string | number;
  menuItems: React.ReactNode[] | undefined;
  handleEditTimesheet?: () => void;
  handleApproveTimesheet?: () => void;
  disableCheckbox: boolean;
  isSelecting?: boolean;
  setIsSelecting?: (isSelect: boolean) => void;
  setCheckboxIds?: (ids: any) => void;
  checkboxIds: string[];
  timesheet?: TimesheetItem;
  handleAddComment?: () => void;
  isClientAdmin: boolean;
  job: GetJobQuery['job'];
  jobStatus: JobStatus;
  relationshipStatus: RelationshipKindEnum | null;
  createWorkerRelationship: (
    reason: string,
    comments: string,
    kind: RelationshipKindEnum,
  ) => void;
  deleteWorkerRelationship: (kind: RelationshipKindEnum) => void;
  isMissingTimesheet?: boolean;
  isMissingCheckout?: boolean;
}

const WorkerMobileView = ({
  workerInfo,
  disableEdit,
  totalTime = '-',
  menuItems = [],
  handleEditTimesheet,
  handleApproveTimesheet,
  disableCheckbox,
  isSelecting,
  setIsSelecting,
  checkboxIds,
  setCheckboxIds,
  timesheet,
  handleAddComment,
  isClientAdmin,
  workerStatusChip,
  job,
  jobStatus,
  relationshipStatus,
  createWorkerRelationship,
  deleteWorkerRelationship,
  isMissingTimesheet,
  isMissingCheckout,
}: Props) => {
  const name = workerName(workerInfo);
  const MENU_ITEMS = [...menuItems];

  const showRating = useFeatureValue(FEATURE_TOGGLE.WorkerRating, false);
  const profileRevamp = useFeatureValue(
    FEATURE_TOGGLE.WorkerProfileRevamp,
    false,
  );

  const [showOption, hideOptionModal] = useModal(
    () => (
      <OptionsModal
        hideModal={hideOptionModal}
        menuItems={MENU_ITEMS}
        title={name}
      />
    ),
    [MENU_ITEMS, name],
  );
  const [showProfModal, hideProfModal] = useModal(() => (
    <ProfileModal ProfileInfo={workerInfo} hideModal={hideProfModal} />
  ));

  const [showProfileRevampModal, hideProfileRevampModal] = useModal(
    () => (
      <ProfileModalRevamp
        ProfileInfo={workerInfo}
        hideModal={hideProfileRevampModal}
        isClientAdmin={isClientAdmin}
        relationshipStatus={relationshipStatus}
        createWorkerRelationship={createWorkerRelationship}
        deleteWorkerRelationship={deleteWorkerRelationship}
      />
    ),
    [workerInfo, isClientAdmin, relationshipStatus],
  );

  // Resolve modal will be handled within WorkerTimesheet where the button lives

  const navigate = useNavigate();
  const handleWorkerClick = () => {
    if (isClientAdmin && showRating) {
      profileRevamp ? showProfileRevampModal() : showProfModal();
    }
    if (!isClientAdmin) {
      profileRevamp
        ? showProfileRevampModal()
        : navigate(`/workers/${workerInfo.id}`);
    }
  };

  const holdTimer = useRef<any | null>(null);
  const checked = checkboxIds.includes(workerInfo.workerId);

  const startHold = useCallback(() => {
    if (disableCheckbox || isSelecting) return;

    holdTimer.current = setTimeout(() => {
      setCheckboxIds?.((ids) => [...ids, workerInfo.workerId]);
      setIsSelecting?.(true);
    }, 1000);
  }, [disableCheckbox, isSelecting]);

  const cancelHold = useCallback(() => {
    clearTimeout(holdTimer.current);
    holdTimer.current = null;
  }, []);

  const handleClick = () => {
    if (isSelecting && !disableCheckbox) {
      if (checked) {
        setCheckboxIds?.((ids) => ids.filter((i) => i !== workerInfo.workerId));
      } else {
        setCheckboxIds?.((ids) => [...ids, workerInfo.workerId]);
      }
    }
  };

  const isCertified = containsGravyCertification(workerInfo.activeCertificates);

  // Find the jobWorker to check if worker is dismissed or removed
  const jobWorker = job?.jobWorkers?.find((x) => x.worker.id === workerInfo.id);
  const isDismissed = jobWorker ? !!jobWorker.dismissType : false;
  const isRemoved = jobWorker
    ? jobWorker.status === JobWorkerStatusEnum.DISMISSED
    : false;

  // Get dismissed tooltip content
  const getDismissedTooltipContent = () => {
    if (!jobWorker || !jobWorker.dismissType) return null;

    const dismissedByName = jobWorker.dismissedBy
      ? `${jobWorker.dismissedBy.firstName || ''} ${jobWorker.dismissedBy.lastName || ''}`.trim()
      : '';

    let dismissedAtFormatted = '';
    if (jobWorker.dismissedAt) {
      try {
        const dismissedDate = parseISO(jobWorker.dismissedAt);
        dismissedAtFormatted = format(dismissedDate, 'h:mm a, MMM dd, yyyy');
      } catch (e) {
        dismissedAtFormatted = jobWorker.dismissedAt;
      }
    }

    return {
      dismissType: jobWorker.dismissType,
      dismissReason: jobWorker.dismissReason || '',
      dismissedByName,
      dismissedAtFormatted,
    };
  };

  // Get removed tooltip content
  const getRemovedTooltipContent = () => {
    if (!jobWorker || !isRemoved) return null;

    const removedByName = jobWorker.dismissedBy
      ? `${jobWorker.dismissedBy.firstName || ''} ${jobWorker.dismissedBy.lastName || ''}`.trim()
      : '';

    let removedAtFormatted = '';
    if (jobWorker.dismissedAt) {
      try {
        const removedDate = parseISO(jobWorker.dismissedAt);
        removedAtFormatted = format(removedDate, 'h:mm a, MMM dd, yyyy');
      } catch (e) {
        removedAtFormatted = jobWorker.dismissedAt;
      }
    }

    return {
      reason: jobWorker.dismissReason || '',
      removedByName,
      removedAtFormatted,
    };
  };

  const dismissedTooltipContent = getDismissedTooltipContent();
  const removedTooltipContent = getRemovedTooltipContent();

  // Tooltip state
  const [anchorDismissedTooltip, setAnchorDismissedTooltip] = useState(null);
  const [anchorRemovedTooltip, setAnchorRemovedTooltip] = useState(null);
  const handleDismissedPopoverOpen = (event) => {
    setAnchorDismissedTooltip(event.currentTarget);
  };
  const handleDismissedPopoverClose = () => {
    setAnchorDismissedTooltip(null);
  };
  const handleRemovedPopoverOpen = (event) => {
    setAnchorRemovedTooltip(event.currentTarget);
  };
  const handleRemovedPopoverClose = () => {
    setAnchorRemovedTooltip(null);
  };
  const openDismissedTooltip = Boolean(anchorDismissedTooltip);
  const openRemovedTooltip = Boolean(anchorRemovedTooltip);

  return (
    <>
      <Stack
        style={{
          opacity: 1,
          width: '100%',
        }}
        onTouchEnd={cancelHold}
        onTouchStart={startHold}
      >
        <Stack
          alignItems={isSelecting ? 'center' : 'flex-start'}
          borderBottom={'1px solid rgba(121, 192, 167, 0.90)'}
          borderRadius="12px 13px 0px 0px"
          flexDirection="row"
          gap={isSelecting ? '24px' : '6px'}
          padding="13px"
          style={{
            background: 'linear-gradient(270deg, #EFFFED -78.65%, #FFF 75.74%)',
          }}
        >
          <Stack>
            <Grid item xs={profileRevamp ? 3 : 2} onClick={handleClick}>
              {isSelecting && !disableCheckbox ? (
                <div id="checkbox" style={{ left: 7, top: 3 }}>
                  {checked ? (
                    <CheckCircle htmlColor={timesheetColors.selected} />
                  ) : (
                    <RadioButtonUncheckedOutlined
                      htmlColor={timesheetColors.unSelected}
                    />
                  )}
                </div>
              ) : profileRevamp ? (
                <div style={{ position: 'relative' }}>
                  <Avatar
                    border={isCertified}
                    rounded={true}
                    size="m"
                    src={workerInfo.avatarUrl}
                  />
                  {isCertified && (
                    <GravyTrained
                      sx={{
                        position: 'absolute',
                        right: 0,
                        bottom: 0,
                        width: '15px',
                        height: '15px',
                      }}
                    />
                  )}
                </div>
              ) : (
                <Avatar size="m" src={workerInfo.avatarUrl} />
              )}
            </Grid>
          </Stack>
          <Stack
            alignItems={'flex-start'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            width={'100%'}
          >
            <Stack flexDirection={'column'} gap={'2px'}>
              <Stack
                flexDirection={'row'}
                gap={'8px'}
                style={{ paddingRight: '5px' }}
              >
                <CardHeading
                  onClick={
                    !isSelecting && showRating ? handleWorkerClick : undefined
                  }
                >
                  {name}
                </CardHeading>
                {relationshipStatus === RelationshipKindEnum.BLOCKED ? (
                  <BlockIconWithBackground />
                ) : (
                  <PermissionComponent id="toggle-favorite-btn" showAsReadOnly>
                    <IconicButton
                      a11yLabel="Toggle Favorite"
                      appearance="clear"
                      transparentOnHover={true}
                      customIcon={
                        relationshipStatus ===
                        RelationshipKindEnum.FAVORITED ? (
                          <HeartFillIcon
                            style={{
                              cursor: 'pointer',
                            }}
                          />
                        ) : (
                          <HeartIcon
                            style={{
                              cursor: 'pointer',
                            }}
                          />
                        )
                      }
                      data-testid="toggle-favorite"
                      icon={faCircle}
                      id="toggle-favorite-btn"
                      size="sm"
                      type="button"
                      onClick={() => {
                        if (
                          relationshipStatus === RelationshipKindEnum.FAVORITED
                        ) {
                          deleteWorkerRelationship(
                            RelationshipKindEnum.FAVORITED,
                          );
                        } else {
                          createWorkerRelationship(
                            '',
                            '',
                            RelationshipKindEnum.FAVORITED,
                          );
                        }
                      }}
                    />
                  </PermissionComponent>
                )}
              </Stack>
              <Stack alignItems={'center'} flexDirection={'row'} gap={'8px'}>
                {profileRevamp && (
                  <Stack alignItems={'center'} direction={'row'} gap={'2px'}>
                    <StarIcon
                      id="star-icon"
                      sx={{
                        color:
                          workerInfo.averageRating != null &&
                          Number(workerInfo.averageRating) > 0
                            ? '#faaf00'
                            : '#D1D5DB',
                        width: '16px',
                        height: '16px',
                      }}
                    />
                    {workerInfo.averageRating != null &&
                    Number(workerInfo.averageRating) > 0 ? (
                      <>
                        <CardCriteria>
                          {Number(workerInfo.averageRating).toFixed(1)}
                        </CardCriteria>
                        {workerInfo.jobsRatingCount != null &&
                          Number(workerInfo.jobsRatingCount) > 0 && (
                            <CardCriteriaTotal>{`(${workerInfo.jobsRatingCount})`}</CardCriteriaTotal>
                          )}
                      </>
                    ) : (
                      <div style={{ marginLeft: '-2px' }}>
                        <CardCriteria>
                          <span style={{ color: '#D1D5DB' }}>-</span>
                        </CardCriteria>
                      </div>
                    )}
                  </Stack>
                )}

                <WorkerPayRate
                  clientAdmin={isClientAdmin}
                  job={job}
                  timesheet={timesheet}
                  workerId={workerInfo.id}
                />
              </Stack>
              {/* Note: Rating End */}
            </Stack>

            <Stack alignItems={'flex-start'} flexDirection={'row'} gap={'10px'}>
              {isRemoved && removedTooltipContent ? (
                <>
                  <Popover
                    disableRestoreFocus
                    anchorEl={anchorRemovedTooltip}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={openRemovedTooltip}
                    slotProps={{
                      paper: {
                        style: {
                          maxWidth: '269px',
                          width: '269px',
                          boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.12)',
                          border: '1px solid #E2E2E2',
                        },
                        className: 'afterArrow',
                      },
                    }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPopover-paper': {
                        marginBottom: '16px',
                        transform: 'translateY(-100%)',
                      },
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    onClose={handleRemovedPopoverClose}
                  >
                    <div style={{ padding: '12px 0px 20px 0px' }}>
                      {removedTooltipContent.reason && (
                        <div
                          style={{
                            display: 'block',
                            padding: '0px 12px 0px 16px',
                          }}
                        >
                          <div
                            style={{
                              color: 'black',
                              lineHeight: 'normal',
                              letterSpacing: '.17px',
                              padding: '0px 0px',
                              fontSize: '12px',
                              fontWeight: 400,
                              width: '150px',
                            }}
                          >
                            {removedTooltipContent.reason}
                          </div>
                        </div>
                      )}
                      {removedTooltipContent.removedByName &&
                        removedTooltipContent.removedAtFormatted && (
                          <div
                            style={{
                              display: 'block',
                              padding: '0px 12px 0px 16px',
                            }}
                          >
                            <div
                              style={{
                                color: 'black',
                                lineHeight: 'normal',
                                letterSpacing: '.17px',
                                padding: '0px 0px',
                                fontSize: '12px',
                                fontWeight: 400,
                                width: '150px',
                              }}
                            >
                              by {removedTooltipContent.removedByName},{' '}
                              {removedTooltipContent.removedAtFormatted}
                            </div>
                          </div>
                        )}
                    </div>
                  </Popover>
                  <MuiChip
                    icon={
                      <InfoItalicIcon
                        style={{
                          color: '#5C5C5C',
                          height: '16px',
                          width: '16px',
                        }}
                        onMouseEnter={handleRemovedPopoverOpen}
                        onMouseLeave={handleRemovedPopoverClose}
                      />
                    }
                    label="Removed"
                    size="small"
                    sx={{
                      fontSize: '0.8rem',
                      bgcolor: '#DDE1E0',
                      color: '#5C5C5C',
                      paddingLeft: '4px',
                      borderRadius: '8px',
                    }}
                  />
                </>
              ) : (
                workerStatusChip
              )}
              {isMissingTimesheet && MissingTimesheetChip}
              {isMissingCheckout && MissingCheckoutChip}
              {isDismissed && dismissedTooltipContent && (
                <>
                  <Popover
                    disableRestoreFocus
                    anchorEl={anchorDismissedTooltip}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={openDismissedTooltip}
                    slotProps={{
                      paper: {
                        style: {
                          maxWidth: '269px',
                          width: '269px',
                          boxShadow: '0px 0px 12px rgba(0, 0, 0, 0.12)',
                          border: '1px solid #E2E2E2',
                        },
                        className: 'afterArrow',
                      },
                    }}
                    sx={{
                      pointerEvents: 'none',
                      '& .MuiPopover-paper': {
                        marginBottom: '16px',
                        transform: 'translateY(-100%)',
                      },
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    onClose={handleDismissedPopoverClose}
                  >
                    <div style={{ padding: '12px 0px 20px 0px' }}>
                      <div
                        style={{
                          display: 'block',
                          padding: '0px 12px 0px 16px',
                        }}
                      >
                        <div
                          style={{
                            color: 'black',
                            lineHeight: 'normal',
                            letterSpacing: '.17px',
                            padding: '0px 0px',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {dismissedTooltipContent.dismissType}
                        </div>
                      </div>
                      {dismissedTooltipContent.dismissReason && (
                        <div
                          style={{
                            display: 'block',
                            padding: '0px 12px 0px 16px',
                          }}
                        >
                          <div
                            style={{
                              color: 'black',
                              lineHeight: 'normal',
                              letterSpacing: '.17px',
                              fontSize: 12,
                              fontWeight: 400,
                              width: '150px',
                            }}
                          >
                            {dismissedTooltipContent.dismissReason}
                          </div>
                        </div>
                      )}
                      {dismissedTooltipContent.dismissedByName &&
                        dismissedTooltipContent.dismissedAtFormatted && (
                          <div
                            style={{
                              display: 'block',
                              padding: '0px 12px 0px 16px',
                            }}
                          >
                            <div
                              style={{
                                color: 'black',
                                lineHeight: 'normal',
                                letterSpacing: '.17px',
                                fontSize: 12,
                                fontWeight: 400,
                                width: '150px',
                              }}
                            >
                              by {dismissedTooltipContent.dismissedByName},{' '}
                              {dismissedTooltipContent.dismissedAtFormatted}
                            </div>
                          </div>
                        )}
                    </div>
                  </Popover>
                  <MuiChip
                    icon={
                      <InfoItalicIcon
                        style={{
                          color: '#1C1C1C',
                          height: '16px',
                          width: '16px',
                        }}
                        onMouseEnter={handleDismissedPopoverOpen}
                        onMouseLeave={handleDismissedPopoverClose}
                      />
                    }
                    label="Dismissed"
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      background: '#F4F4F4',
                      display: 'flex',
                      padding: '0 8px',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#1C1C1C',
                      fontFamily: 'Manrope',
                      fontSize: '11px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '15.4px',
                      textTransform: 'uppercase',
                    }}
                  />
                </>
              )}
              <PermissionComponent id="timesheet-options-menu">
                <IconButton
                  disabled={!MENU_ITEMS.length}
                  style={{ padding: '1px' }}
                  onClick={showOption}
                >
                  <MoreVert />
                </IconButton>
              </PermissionComponent>
            </Stack>
          </Stack>
        </Stack>
        <WorkerTimesheet
          disableEdit={disableEdit}
          handleAddComment={handleAddComment}
          handleApproveTimesheet={handleApproveTimesheet}
          handleEditTimesheet={handleEditTimesheet}
          isClientAdmin={isClientAdmin}
          isSelecting={isSelecting}
          job={job}
          jobStatus={jobStatus}
          phoneOnly={true}
          timesheet={timesheet}
          totalTime={totalTime}
          workerInfo={workerInfo}
          workerStatusChip={workerStatusChip}
          isMissingTimesheet={isMissingTimesheet}
          isMissingCheckout={isMissingCheckout}
        />
      </Stack>
    </>
  );
};

interface WorkerTimesheetProps {
  workerInfo: any;
  disableEdit?: boolean;
  workerStatusChip?: React.ReactNode;
  totalTime?: string | number;
  handleEditTimesheet?: () => void;
  handleApproveTimesheet?: () => void;
  isSelecting?: boolean;
  setIsSelecting?: (isSelect: boolean) => void;
  timesheet?: TimesheetItem;
  handleAddComment?: () => void;
  isClientAdmin: boolean;
  job: GetJobQuery['job'];
  jobStatus: JobStatus;
  phoneOnly: boolean;
  isMissingTimesheet?: boolean;
  isMissingCheckout?: boolean;
}

export const WorkerTimesheet = ({
  isSelecting,
  disableEdit,
  timesheet,
  isClientAdmin,
  jobStatus,
  workerStatusChip,
  job,
  workerInfo,
  totalTime,
  handleAddComment,
  handleApproveTimesheet,
  handleEditTimesheet,
  phoneOnly,
  isMissingTimesheet,
  isMissingCheckout,
}: WorkerTimesheetProps) => {
  const [inlineEditing, setInlineEditing] = useState(false);
  const tsStatus = getTimeSheetStatus(timesheet);

  // Find the jobWorker to check if worker is tagged as no-show
  const jobWorker = job?.jobWorkers?.find(
    (jw) =>
      jw.worker.id === workerInfo.id || jw.id === timesheet?.jobWorker?.id,
  );
  const isNoShow = jobWorker ? !jobWorker.showed : false;

  const [openResolveModal, closeResolveModal] = useModal(
    () => (
      <ResolveTimesheetModal
        open={true}
        onClose={closeResolveModal}
        workerFullName={`${workerInfo.user?.firstName || ''} ${
          workerInfo.user?.lastName || ''
        }`.trim()}
        hasMissingCheckout={
          !!(timesheet?.jobWorker?.isMissingCheckout || isMissingCheckout)
        }
        hasMissingTimesheet={
          !!(timesheet?.jobWorker?.isMissingTimesheet || isMissingTimesheet)
        }
        isClientAdmin={isClientAdmin}
        timesheet={timesheet}
        job={job}
        workerInfo={workerInfo}
      />
    ),
    [
      workerInfo,
      timesheet,
      isMissingCheckout,
      isMissingTimesheet,
      isClientAdmin,
      job,
    ],
  );

  const allowInlineEditing = () => {
    const result =
      !isSelecting &&
      !disableEdit &&
      (!timesheet ||
        (timesheet &&
          timesheet.status !== TimesheetStatusEnum.APPROVED &&
          timesheet.status !== TimesheetStatusEnum.REJECTED));

    console.log('=== ALLOW INLINE EDITING DEBUG ===');
    console.log('isSelecting:', isSelecting);
    console.log('disableEdit:', disableEdit);
    console.log('timesheet:', timesheet);
    console.log('timesheet?.status:', timesheet?.status);
    console.log('TimesheetStatusEnum.APPROVED:', TimesheetStatusEnum.APPROVED);
    console.log('TimesheetStatusEnum.REJECTED:', TimesheetStatusEnum.REJECTED);
    console.log('result:', result);
    console.log('==================================');

    return result;
  };

  const hasComments = () => {
    if (
      timesheet &&
      timesheet.timesheetComments &&
      timesheet.timesheetComments.length
    ) {
      if (isClientAdmin) {
        return (
          timesheet.timesheetComments.filter(
            (c) => c.actorType === 'CustomerAdmin',
          ).length > 0
        );
      }
      return true;
    }
    return false;
  };

  const showReviewButton = () => {
    return (
      (jobStatus !== JobStatus.UPCOMING ||
        (jobStatus === JobStatus.UPCOMING &&
          typeof workerStatusChip === typeof HiredChip)) &&
      (jobStatus !== JobStatus.CANCELLED ||
        (jobStatus === JobStatus.CANCELLED && timesheet)) &&
      (!timesheet ||
        (timesheet &&
          timesheet.status !== TimesheetStatusEnum.APPROVED &&
          timesheet.status !== TimesheetStatusEnum.REJECTED))
    );
  };

  const timesheetScrutiny = useFeatureValue(
    FEATURE_TOGGLE.TimesheetScrutiny,
    false,
  );

  const { currentAdminIsCustomerAdmin } = useAuth();

  const [flagMessageTooltip, setFlagMessageTooltip] = useState(false);

  const handleFlagMessageDialogOpen = () => {
    setFlagMessageTooltip(true);
  };

  const handleFlagMessageDialogClose = () => {
    setFlagMessageTooltip(false);
  };

  const getNeedsApprovalOrReviewChip = () => {
    const needsReview =
      timesheetScrutiny &&
      !currentAdminIsCustomerAdmin &&
      timesheet &&
      timesheet.flagReason !== 0;

    if (needsReview) {
      return (
        <ApprovalAlert style={{ backgroundColor: colors.needsReview }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoItalicIcon
              style={{
                color: '#fff',
                height: '18px',
                width: '18px',
              }}
              onClick={handleFlagMessageDialogOpen}
            />
            <ScrutinyDialogPhoneView
              headerText="Details"
              infoText={timesheetFlagMessage(timesheet, job.address)}
              open={flagMessageTooltip}
              onClose={handleFlagMessageDialogClose}
            />
            <NoWrapSpan>Needs Review</NoWrapSpan>
          </Stack>
        </ApprovalAlert>
      );
    }
    return <ApprovalAlert>Needs Approval</ApprovalAlert>;
  };

  return (
    <Stack
      direction={phoneOnly ? 'column' : 'row'}
      padding={phoneOnly ? '13px' : '0px'}
      style={
        phoneOnly
          ? {}
          : {
              justifyContent: 'flex-end',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: '20px',
              rowGap: '10px',
            }
      }
    >
      <InlineTimesheetView
        allowEdit={allowInlineEditing()}
        job={job}
        phoneOnly={phoneOnly}
        setInlineEditing={setInlineEditing}
        timesheet={timesheet}
        totalTime={!phoneOnly ? totalTime : undefined}
        workerInfo={workerInfo}
        isMissingTimesheet={isMissingTimesheet}
        isMissingCheckout={isMissingCheckout}
      />
      {phoneOnly && (
        <Stack
          alignItems={'center'}
          flexDirection={'row'}
          justifyContent={'space-between'}
          padding={'10px 0 3px 9px'}
        >
          <TotalText>
            Total:{' '}
            <span>
              {totalTime !== '-'
                ? typeof totalTime === 'number'
                  ? `${totalTime.toFixed(2)} hours`
                  : `${totalTime} hours`
                : '-'}
            </span>
          </TotalText>
          {showNeedsApprovalAndReviewChip(workerInfo, timesheet, job) ? (
            getNeedsApprovalOrReviewChip()
          ) : tsStatus === TimeSheetStatus.APPROVED ||
            tsStatus === TimeSheetStatus.REJECTED ? (
            <Stack alignItems={'center'} flexDirection={'row'} gap={'4px'}>
              <ApprovedByIcon
                style={{
                  width: '16px',
                  height: '14px',
                }}
              />{' '}
              {timesheet?.updatedBy ? (
                <>
                  {`${
                    timesheet.updatedBy.firstName
                  } ${timesheet.updatedBy.lastName?.substring(0, 1)}.`}
                </>
              ) : timesheet?.updatedByType === 'GravyWorkSystem' ? (
                tsStatus === TimeSheetStatus.APPROVED ? (
                  'System Approved'
                ) : (
                  'System Rejected'
                )
              ) : (
                '-'
              )}
            </Stack>
          ) : null}
        </Stack>
      )}

      <Stack
        alignItems={phoneOnly ? 'normal' : 'flex-end'}
        gap={'4px'}
        style={{ marginTop: phoneOnly ? 5 : 0 }}
      >
        <Stack flexDirection={'row'} gap={'8px'}>
          {timesheet?.timesheetComments?.length || timesheet?.rating ? (
            <div
              id="rating-section"
              style={{ cursor: 'pointer', width: '50%' }}
              onClick={() => timesheet && handleAddComment?.()}
            >
              <Stack
                className={timesheet ? '' : 'rating-stack rating-disabled'}
                direction={'row'}
                spacing={2}
                sx={{
                  border: '1px solid #44A735',
                  borderRadius: '4px',
                  padding: '6.8px 12px',
                  justifyContent: 'center',
                }}
              >
                <RatingField
                  initialRating={timesheet?.rating || 0}
                  readOnly={true}
                  showNumberLabel={false}
                  starSize="16"
                />
                {hasComments() ? (
                  <Stack
                    gap={'0'}
                    marginLeft={'6px !important'}
                    position={'relative'}
                    style={{ justifyContent: 'center', alignItems: 'center' }}
                  >
                    <CommentCounterIcon
                      style={{
                        fontSize: '18px',
                        margin: '0px',
                      }}
                    />
                    <Counter>{timesheet?.timesheetComments?.length}</Counter>
                  </Stack>
                ) : (
                  <div className="icon-add-rating">
                    <svg
                      fill="none"
                      height="17"
                      viewBox="0 0 19 17"
                      width="19"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8.99996 0.5C13.5833 0.5 17.3333 3.48333 17.3333 7.16667C17.3333 7.65 17.2666 8.11667 17.15 8.56667C16.6583 8.31667 16.125 8.13333 15.5666 8.05C15.6333 7.76667 15.6666 7.46667 15.6666 7.16667C15.6666 4.40833 12.6833 2.16667 8.99996 2.16667C5.31663 2.16667 2.33329 4.40833 2.33329 7.16667C2.33329 9.925 5.31663 12.1667 8.99996 12.1667L9.90829 12.125L9.83329 13L9.89996 13.7917L8.99996 13.8333C8.00829 13.8333 7.01663 13.6917 6.05829 13.4167C4.53329 14.6667 2.64163 15.4083 0.666626 15.5C2.60829 13.5583 2.95829 12.25 2.95829 11.75C1.54996 10.6417 0.708293 8.95833 0.666626 7.16667C0.666626 3.48333 4.41663 0.5 8.99996 0.5ZM14 9.66667H15.6666V12.1667H18.1666V13.8333H15.6666V16.3333H14V13.8333H11.5V12.1667H14V9.66667Z"
                        fill="#A1A1A1"
                      />
                    </svg>
                  </div>
                )}
              </Stack>
            </div>
          ) : (
            <>
              <IconicButton
                a11yLabel="Rate Worker"
                appearance="outline"
                customIcon={
                  <>
                    <AddRatingIcon
                      style={{
                        height: '15px',
                        width: '15px',
                        color:
                          !timesheet || disableEdit || inlineEditing
                            ? '#97A0AF'
                            : '#44A735',
                      }}
                    />{' '}
                  </>
                }
                disabled={!timesheet || disableEdit || inlineEditing}
                icon={faCircle}
                iconTheme={'default'}
                id="btn_more_filters"
                size={'full'}
                style={{
                  padding: '8px 25px',
                  width: '50%',
                }}
                text="Rate Worker"
                onClick={() => handleAddComment && handleAddComment()}
              />
            </>
          )}
          {showReviewButton() ? (
            <>
              {isMissingTimesheet || isMissingCheckout ? (
                isNoShow ? (
                  <IconicButton
                    a11yLabel={'Approve'}
                    customIcon={
                      <>
                        <img
                          src={require('@/assets/icons/document-check.svg')}
                          alt="Approve"
                          style={{
                            width: '24px',
                            height: '24px',
                            flexShrink: 0,
                          }}
                        />{' '}
                      </>
                    }
                    disabled={true}
                    icon={faCircle}
                    iconTheme={'default'}
                    id="btn-approve"
                    size={'full'}
                    style={{
                      borderRadius: '4px',
                      background: '#9E9E9E',
                      display: 'flex',
                      width: '114px',
                      height: '32px',
                      padding: '0 16px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      borderColor: '#9E9E9E',
                    }}
                    text={'Approve'}
                    textStyle={{
                      color: '#FFF',
                      textAlign: 'center',
                      fontFamily: 'Manrope',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '16.8px',
                    }}
                    onClick={() => {}}
                  />
                ) : (
                  <IconicButton
                    a11yLabel={'Resolve'}
                    customIcon={
                      <>
                        <img
                          src={require('@/assets/icons/document-check.svg')}
                          alt="Resolve"
                          style={{
                            width: '24px',
                            height: '24px',
                            flexShrink: 0,
                          }}
                        />{' '}
                      </>
                    }
                    disabled={false}
                    icon={faCircle}
                    iconTheme={'default'}
                    id="btn-resolve"
                    size={'full'}
                    style={{
                      borderRadius: '4px',
                      background: '#F45700',
                      display: 'flex',
                      width: '114px',
                      height: '32px',
                      padding: '0 16px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '8px',
                      borderColor: '#F45700',
                    }}
                    text={'Resolve'}
                    textStyle={{
                      color: '#FFF',
                      textAlign: 'center',
                      fontFamily: 'Manrope',
                      fontSize: '12px',
                      fontStyle: 'normal',
                      fontWeight: 600,
                      lineHeight: '16.8px',
                    }}
                    onClick={() => openResolveModal()}
                  />
                )
              ) : (
                <IconicButton
                  a11yLabel={'Approve'}
                  customIcon={
                    <>
                      <EditTimesheetIcon
                        style={{
                          color:
                            disableEdit ||
                            inlineEditing ||
                            (isClientAdmin && !timesheet)
                              ? '#97A0AF'
                              : 'white',
                          height: '15px',
                          width: '15px',
                        }}
                      />{' '}
                    </>
                  }
                  disabled={
                    disableEdit ||
                    inlineEditing ||
                    (isClientAdmin &&
                      !(
                        timesheet?.checkoutAt ||
                        timesheet?.reportedCheckoutAt ||
                        timesheet?.modifiedCheckoutAt
                      ))
                  }
                  icon={faCircle}
                  iconTheme={'default'}
                  id="btn_more_filters"
                  size={'full'}
                  style={{
                    padding: '8px 18px',
                    width: '50%',
                  }}
                  text={'Approve'}
                  onClick={() => {
                    if (isClientAdmin) {
                      handleApproveTimesheet && handleApproveTimesheet();
                    } else {
                      handleEditTimesheet && handleEditTimesheet();
                    }
                  }}
                />
              )}
            </>
          ) : null}
          {timesheet?.status === TimesheetStatusEnum.APPROVED && (
            <>
              <IconicButton
                a11yLabel="Approved"
                appearanceMod={'black'}
                disabled={disableEdit}
                icon={faCheck}
                iconTheme={'default'}
                id="btn_more_filters"
                size={'full'}
                style={{
                  padding: '8px 18px',
                  width: '50%',
                }}
                text="Approved"
                onClick={() => {
                  if (!isClientAdmin && handleEditTimesheet) {
                    handleEditTimesheet();
                  }
                }}
              />
            </>
          )}
          {timesheet?.status === TimesheetStatusEnum.REJECTED && (
            <>
              <IconicButton
                a11yLabel="Rejected"
                appearanceMod={'danger'}
                disabled={disableEdit}
                icon={faTimes}
                iconTheme={'default'}
                id="btn_more_filters"
                size={'full'}
                style={{
                  padding: '8px 18px',
                  width: '50%',
                }}
                text="Rejected"
                onClick={() => {
                  if (!isClientAdmin && handleEditTimesheet) {
                    handleEditTimesheet();
                  }
                }}
              />
            </>
          )}
        </Stack>
        {!phoneOnly &&
        (timesheet?.status === TimesheetStatusEnum.APPROVED ||
          timesheet?.status === TimesheetStatusEnum.REJECTED) ? (
          <Stack
            alignItems={'center'}
            flexDirection={'row'}
            gap={'4px'}
            justifyContent={'flex-end'}
          >
            <ApprovedByIcon
              style={{
                width: '16px',
                height: '14px',
              }}
            />{' '}
            {timesheet?.updatedBy ? (
              <>
                {`${
                  timesheet.updatedBy.firstName
                } ${timesheet.updatedBy.lastName?.substring(0, 1)}.`}
              </>
            ) : timesheet?.updatedByType === 'GravyWorkSystem' ? (
              timesheet.status === TimesheetStatusEnum.APPROVED ? (
                'System Approved'
              ) : (
                'System Rejected'
              )
            ) : (
              '-'
            )}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default WorkerMobileView;
